'use client'

import * as React from 'react'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  type Table as TanstackTable,
  type Column,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react'
// TODO(icons): approx — was Lucide ArrowUpDownIcon
import { ArrowDownIcon, ArrowUpIcon, SortIcon, ChevronLeftIcon, ChevronRightIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'
import { Button } from '@/registry/reapit/ui/button'
import { Flex, Stack } from '@/registry/reapit/ui/flex'
import { Input } from '@/registry/reapit/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/registry/reapit/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/registry/reapit/ui/table'

// ---------------------------------------------------------------------------
// Props types
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DataTable {
  export interface Props<TData> {
    columns: ColumnDef<TData, unknown>[]
    data: TData[]
    searchKey?: string
    searchPlaceholder?: string
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DataTableColumnHeader {
  export interface Props<TData, TValue> extends React.ComponentProps<'div'> {
    column: Column<TData, TValue>
    title: string
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DataTablePagination {
  export interface Props<TData> {
    table: TanstackTable<TData>
  }
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

function DataTable<TData>({ columns, data, searchKey, searchPlaceholder = 'Filter…' }: DataTable.Props<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  return (
    <Stack data-slot="data-table" gap={4}>
      {searchKey && (
        <Flex align="center">
          <Input
            placeholder={searchPlaceholder}
            value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ''}
            onChange={(event) => table.getColumn(searchKey)?.setFilterValue(event.target.value)}
            className="max-w-sm"
          />
        </Flex>
      )}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// DataTableColumnHeader
// ---------------------------------------------------------------------------

function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  ...props
}: DataTableColumnHeader.Props<TData, TValue>) {
  if (!column.getCanSort()) {
    return (
      <span className={cn(className)} {...props}>
        {title}
      </span>
    )
  }

  return (
    <Flex align="center" gap={2} className={cn(className)} {...props}>
      <Button
        variant="tertiary"
        size="small"
        className="-ml-3 h-8 data-[state=open]:bg-accent"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        <span>{title}</span>
        {column.getIsSorted() === 'desc' ? (
          <ArrowDownIcon className="ml-1 size-4" />
        ) : column.getIsSorted() === 'asc' ? (
          <ArrowUpIcon className="ml-1 size-4" />
        ) : (
          <SortIcon size="sm" className="ml-1" />
        )}
      </Button>
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// DataTablePagination
// ---------------------------------------------------------------------------

function DataTablePagination<TData>({ table }: DataTablePagination.Props<TData>) {
  return (
    <Flex data-slot="data-table-pagination" align="center" between className="px-2">
      <span className="flex-1 text-sm text-muted-foreground">
        {table.getFilteredSelectedRowModel().rows.length} of {table.getFilteredRowModel().rows.length} row(s) selected.
      </span>
      <Flex align="center" gap={6}>
        <Flex align="center" gap={2}>
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value))
            }}
          >
            <SelectTrigger size="sm" className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Flex>
        <Flex align="center" justify="center" className="w-[100px] text-sm font-medium">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </Flex>
        <Flex align="center" gap={2}>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeftIcon className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="size-4" />
          </Button>
          <Button variant="outline" size="icon-sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRightIcon className="size-4" />
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

export { DataTable, DataTableColumnHeader, DataTablePagination }
